//#region IMPORTS
import { useEffect, useMemo, useState } from "react";
import { promoSortDate, promoYear } from "../../helpers/promoYear";
import { Promo } from "../../models/promo";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import classNames from "../../helpers/classNames";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  closeHandler: () => void;
}
//#endregion

// Body of the "excel export" modal on the home page (admin): first a year,
// then the projects of that year, then download — the backorders of that
// project as Excel (same export as the old inline tile).
//
// Promos are fetched when the modal opens (MainModal only mounts its children
// while open), in one go with a generous limit. The old tile fetched only the
// first 100 on every home-page load, so older projects silently fell off the
// list. Archived (soft-deleted) promos are not in the list — the backend's
// default promo_status is "Active", same as before.
export default function HomeExcelExportModalBody({ closeHandler }: Props) {
  //#region SETUP
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [year, setYear] = useState<string>("");
  const [promoCode, setPromoCode] = useState<string>("");
  const [downloading, setDownloading] = useState<boolean>(false);
  //#endregion

  //#region LOGIC
  useEffect(() => {
    let cancelled = false;
    const fetchPromos = async () => {
      try {
        const params = getAxiosParams({ page: 1, limit: 1000 });
        const response = await agent.Promos.list(params);
        if (!cancelled) setPromos(response.data);
      } catch (error) {
        if (!cancelled) toast.error(`${error}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPromos();
    return () => {
      cancelled = true;
    };
  }, []);

  // Years that have at least one project, newest first. (A promo without any
  // date can't be placed; in practice created_at is always there.)
  const years = useMemo(
    () =>
      Array.from(
        new Set(
          promos
            .map(promoYear)
            .filter((y): y is number => y !== null),
        ),
      ).sort((a, b) => b - a),
    [promos],
  );

  // Projects of the chosen year, newest first, then by code.
  const promosOfYear = useMemo(
    () =>
      promos
        .filter((promo) => String(promoYear(promo)) === year)
        .sort(
          (a, b) =>
            promoSortDate(b).localeCompare(promoSortDate(a)) ||
            a.code.localeCompare(b.code),
        ),
    [promos, year],
  );

  const download = async () => {
    if (!promoCode) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        promo_code: promoCode,
        order_type: "backorder",
      });
      const response = await agent.Orders.downloadspreadsheat(params);
      const url = URL.createObjectURL(new Blob([response]));
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: `${promoCode}.xlsx`,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      setDownloading(false);
    }
  };
  //#endregion

  //#region RENDER
  const selectClass =
    "mt-1 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-slate-600 sm:text-sm sm:leading-6 disabled:opacity-50";

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900">Excel export</h3>
      <p className="mt-1 text-sm text-gray-500">
        Download the backorders of one project as Excel.
      </p>

      <div className="mt-6 flex flex-col gap-4">
        {/* Year */}
        <div>
          <label
            htmlFor="excel-export-year"
            className="block text-sm font-medium text-gray-900"
          >
            Year
          </label>
          <select
            id="excel-export-year"
            className={selectClass}
            value={year}
            disabled={loading || years.length === 0}
            onChange={(e) => {
              setYear(e.target.value);
              setPromoCode("");
            }}
          >
            <option value="" disabled>
              {loading
                ? "Loading projects…"
                : years.length === 0
                  ? "No projects found"
                  : "Choose a year"}
            </option>
            {years.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {/* Project — only once a year is chosen */}
        {year && (
          <div>
            <label
              htmlFor="excel-export-project"
              className="block text-sm font-medium text-gray-900"
            >
              Project
            </label>
            <select
              id="excel-export-project"
              className={selectClass}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            >
              <option value="" disabled>
                Choose a project
              </option>
              {promosOfYear.map((promo) => (
                <option key={promo.id} value={promo.code}>
                  {promo.code}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={closeHandler}
          className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Close
        </button>
        <button
          type="button"
          onClick={download}
          disabled={!promoCode || downloading}
          className={classNames(
            "inline-flex items-center rounded-md px-3 py-1.5 text-sm font-semibold text-white shadow-sm",
            "bg-slate-800 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-slate-800",
          )}
        >
          {downloading ? "downloading…" : "download"}
        </button>
      </div>
    </div>
  );
  //#endregion
}
